import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { site } from "../content/site";
import { handleAnchorClick } from "../lib/scroll";
import { PhoneMockup } from "./PhoneMockup";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.7, delay: 0.12 + i * 0.12, ease: [0.22, 0.61, 0.36, 1] },
  }),
};

export const Hero = () => {
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 56]);

  return (
    <section id="top" ref={sectionRef} className="overflow-hidden bg-white" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-16 sm:px-6 md:pt-24 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10 lg:px-8">
        <div>
          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-xs font-semibold uppercase tracking-[0.14em] text-brand"
            data-testid="hero-eyebrow"
          >
            {site.hero.eyebrow}
          </motion.p>

          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
            {site.hero.headlineLines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineReveal}
                  initial={reduce ? false : "hidden"}
                  animate="visible"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.45 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-gray-600 md:text-lg"
            data-testid="hero-subcopy"
          >
            {site.hero.subcopy}
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.58 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href={site.hero.primaryCta.href}
              data-testid={site.hero.primaryCta.testid}
              onClick={(e) => handleAnchorClick(e, site.hero.primaryCta.href)}
              className="rounded-md bg-brand px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand"
            >
              {site.hero.primaryCta.label}
            </a>
            <a
              href={site.hero.secondaryCta.href}
              data-testid={site.hero.secondaryCta.testid}
              onClick={(e) => handleAnchorClick(e, site.hero.secondaryCta.href)}
              className="rounded-md border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-900 transition-colors duration-200 hover:border-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand"
            >
              {site.hero.secondaryCta.label}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="flex justify-center lg:justify-end"
        >
          <motion.div style={reduce ? undefined : { y: phoneY }} className="rounded-[36px] border border-gray-200 bg-gray-50 p-6 sm:p-8">
            <PhoneMockup screen="home" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
